import { useEffect, useState, useRef, type ChangeEvent } from "react";
import { CameraPawIcon } from "../components/CameraPawIcon";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import {
  PlusCircleSolidIcon, Button, CameraIcon, Field, SelectField } from "@fawkes/ui";
import { ImageIcon, LayoutGrid, List, PawPrint, Search } from "lucide-react";

import type { PhotoAiSuggestion } from "../api/animalPhoto";
import { useAnimalPhotoUrls } from "../api/useAnimalPhotoUrls";
import { analyzeIntakePassport, analyzeIntakePhoto } from "../api/intakePhoto";
import { fotoKlaarzetten } from "../api/fotoKlaarzetten";
import { CameraCapture } from "./animalTiles";
import { ComboBox } from "./ComboBox";

/**
 * wizard-animal-subwizard (parity-audit slice 3): the Jmix per-animal sub-wizard inside the
 * Inboeken "Dieren" step — Zoek dier (structured criteria form) → Zoekresultaten (photo cards) →
 * Dier (details/confirm; full new-animal form). Mirrors src/main AnimalSearchFragment /
 * AnimalSearchResultsFragment / AnimalDetailsFragment, including:
 *  - "Vul minimaal één zoekveld in om te zoeken." (≥1 criterion before searching);
 *  - single exact-15-digit chip match auto-selects and skips the results step;
 *  - zero hits drop straight into the prefilled new-animal form
 *    ("Geen resultaten gevonden – vul de gegevens in voor het nieuwe dier.");
 *  - the full Jmix field set on the new-animal form (chip / ras / kruising / vacht / kleur /
 *    Cas.\/Ster) with the 15-or-empty chip rule and the live duplicate-chip guard
 *    (GET /api/intake/reference/chip-duplicate — the intake-authority twin of the
 *    Basisinformatie check).
 *
 * Search runs client-side over the wizard's GET /api/animals feed (200-row cap — the Jmix screen
 * caps at 250 server-side); the feed now carries kleur/vacht/kruising/gecastreerd/fotoUrl so the
 * result cards and summary-bar thumbnails render the Jmix card facts.
 *
 * Page-local to the wizards on purpose (StepWizard rule): promote only when a third consumer shows up.
 */

export interface SubwizardAnimal {
  id: number;
  naam: string;
  diersoort: string | null;
  ras: string | null;
  geslacht: string | null;
  geboortedatum: string | null;
  chipPasnummer: string | null;
  overleden: boolean;
  kleur: string | null;
  vacht: string | null;
  kruising: boolean;
  gecastreerd: boolean;
  fotoUrl: string | null;
}

/** A confirmed brand-new animal — ids for the payload, labels for the summary-bar row. */
export interface NewAnimalResult {
  naam: string;
  diersoortId: number;
  diersoortLabel: string | null;
  geslacht: string | null;
  geboortedatum: string | null;
  chipPasnummer: string | null;
  rasId: number | null;
  rasLabel: string | null;
  kruising: boolean;
  vachtId: number | null;
  vachtLabel: string | null;
  kleurId: number | null;
  kleurLabel: string | null;
  gecastreerd: boolean;
}

interface OptionRow {
  id: number;
  label: string;
  /** For a breed option: the owning Diersoort id — lets picking a Ras auto-set the species (Jmix
   *  Ras→Diersoort coupling). Null/absent for species/coats/colours. */
  speciesId?: number | null;
}

/** The full animal record behind the editable details form (GET /api/animals/{id}). */
interface AnimalDetail {
  id: number;
  naam: string | null;
  geslacht: string | null;
  gecastreerd: boolean | null;
  chipPasnummer: string | null;
  geboortedatum: string | null;
  diersoortId: number | null;
  rasId: number | null;
  kruising: boolean | null;
  vachtId: number | null;
  kleurId: number | null;
  version: number | null;
}

interface ChipDuplicate {
  duplicate: boolean;
  id: number | null;
  naam: string | null;
}

interface Criteria {
  naam: string;
  chip: string;
  geslacht: string;
  gecastreerd: boolean;
  geboortedatum: string;
  diersoortId: string;
  rasId: string;
  kruising: boolean;
  vachtId: string;
  kleurId: string;
}

const EMPTY_CRITERIA: Criteria = {
  naam: "",
  chip: "",
  geslacht: "",
  gecastreerd: false,
  geboortedatum: "",
  diersoortId: "",
  rasId: "",
  kruising: false,
  vachtId: "",
  kleurId: "",
};

const GESLACHTEN = ["Mannelijk", "Vrouwelijk"];

// AI prefill (parity slice 3b, Jmix AnimalSearchFragment.tryApplyAnimalPhotoAiPrefillToEmptySearchFields):
// a plain photo analysis may fill chip/naam/geslacht/soort/ras/vacht/kleur; the passport scan
// (AnimalPassportPrefillService) additionally carries the birth date. EMPTY fields only, always.
const PHOTO_PREFILL_FIELDS = new Set([
  "CHIPNUMMER", "NAAM", "GESLACHT", "DIERSOORT", "RAS", "VACHT", "VACHT_KLEUR",
]);
const PASSPORT_PREFILL_FIELDS = new Set([...PHOTO_PREFILL_FIELDS, "GEBOORTEDATUM"]);

function isExactly15Digits(v: string): boolean {
  return /^\d{15}$/.test(v.replace(/\s/g, ""));
}

/**
 * Merge AI suggestions into the criteria/draft — EMPTY fields only (the Jmix rule). Dropdown fields
 * apply through the resolved `referenceId` (the api fuzzy-matched the model's free text onto existing
 * reference rows), so the selects land on real options. Pure, so the caller can count what a merge
 * would apply (for the notification) and still update state functionally.
 */
function mergeAiSuggestions(
  cur: Criteria,
  suggestions: PhotoAiSuggestion[],
  allowed: Set<string>,
): { next: Criteria; applied: number } {
  const next = { ...cur };
  let applied = 0;
  for (const s of suggestions) {
    if (!allowed.has(s.field) || !s.value) continue;
    switch (s.field) {
      case "CHIPNUMMER":
        if (!next.chip.trim()) { next.chip = s.value; applied++; }
        break;
      case "NAAM":
        if (!next.naam.trim()) { next.naam = s.value; applied++; }
        break;
      case "GESLACHT":
        if (!next.geslacht && GESLACHTEN.includes(s.value)) { next.geslacht = s.value; applied++; }
        break;
      case "GEBOORTEDATUM":
        if (!next.geboortedatum) { next.geboortedatum = s.value; applied++; }
        break;
      case "DIERSOORT":
        if (!next.diersoortId && s.referenceId != null) { next.diersoortId = String(s.referenceId); applied++; }
        break;
      case "RAS":
        if (!next.rasId && s.referenceId != null) { next.rasId = String(s.referenceId); applied++; }
        break;
      case "VACHT":
        if (!next.vachtId && s.referenceId != null) { next.vachtId = String(s.referenceId); applied++; }
        break;
      case "VACHT_KLEUR":
        if (!next.kleurId && s.referenceId != null) { next.kleurId = String(s.referenceId); applied++; }
        break;
      default:
        break;
    }
  }
  return { next, applied };
}

/** A camera snapshot (data URL) as an upload-ready File — atob keeps this jsdom-safe. */
function dataUrlToFile(dataUrl: string): File {
  const [meta, base64] = dataUrl.split(",");
  const mime = /data:([^;]+)/.exec(meta ?? "")?.[1] ?? "image/jpeg";
  const bin = atob(base64 ?? "");
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return new File([bytes], `camera_${Date.now()}.jpg`, { type: mime });
}

function useFeed<T>(url: string, enabled = true): T[] {
  const { data } = useCustom({ url, method: "get", queryOptions: { enabled } });
  return (data?.data as T[] | undefined) ?? [];
}

/** The Jmix result-card / summary-row photo: plain `online_dier_afbeelding_url`, camera fallback. */
export function AnimalThumb({ fotoUrl, alt, noPhotoLabel }: { fotoUrl: string | null; alt: string; noPhotoLabel: string }) {
  return fotoUrl ? (
    <img className="fawkes-animalsub__thumb" src={fotoUrl} alt={alt} />
  ) : (
    <span className="fawkes-animalsub__thumb fawkes-animalsub__thumb--empty" title={noPhotoLabel} aria-label={noPhotoLabel} />
  );
}

export function AnimalSubwizard({
  animals,
  residentIds,
  selectedIds,
  onAddExisting,
  onAddNew,
}: {
  /** The GET /api/animals feed (superset summary — see SubwizardAnimal). */
  animals: SubwizardAnimal[];
  /** Dier-ids with an open stay — selectable nowhere (Jmix "Dit dier is al in het asiel aanwezig."). */
  residentIds: Set<number>;
  /** Dier-ids already on this intake (Jmix "Dier is al geselecteerd voor deze intake."). */
  selectedIds: Set<number>;
  /** `photo` = the captured animal photo (upload/camera) — becomes the primary Dier photo on completion. */
  onAddExisting: (a: SubwizardAnimal, photo: File | null) => void;
  onAddNew: (n: NewAnimalResult, photo: File | null) => void;
}) {
  const t = useTranslate();
  const [phase, setPhase] = useState<"search" | "results" | "details">("search");
  const [criteria, setCriteria] = useState<Criteria>(EMPTY_CRITERIA);
  const [results, setResults] = useState<SubwizardAnimal[]>([]);
  // Card (photo grid) vs list (table) view of the results, like the Jmix view-mode switch.
  const [resultsView, setResultsView] = useState<"cards" | "list">("cards");
  // Authenticated photos for the result cards (the plain fotoUrl is null for shelter-uploaded animals).
  const resultPhotoUrls = useAnimalPhotoUrls(results.map((a) => a.id));
  const [searchNotice, setSearchNotice] = useState<string | null>(null);
  // details phase target: an existing animal to confirm, or null = the new-animal form
  const [detailsAnimal, setDetailsAnimal] = useState<SubwizardAnimal | null>(null);
  const [fromZeroHits, setFromZeroHits] = useState(false);

  // ── photo + AI prefill (parity slice 3b — Jmix AnimalSearchFragment upload widget) ──────────────
  // The captured photo lives ONLY in React state (never in the WizardData draft JSON) and rides along
  // with onAddExisting/onAddNew; the host uploads it after /complete returned the animal ids.
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [photoNotice, setPhotoNotice] = useState<string | null>(null);
  // Welke bladzijden er zijn uitgelezen. Zonder deze lijst zie je na het scannen alleen "AI heeft
  // gegevens ingevuld" en niet wát er gelezen is -- en met meerdere bladzijden weet je dan niet eens
  // of die tweede foto er wel bij zat.
  const [paspoortBladzijden, setPaspoortBladzijden] = useState<string[]>([]);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const passportInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    // jsdom has no createObjectURL — the preview simply stays empty in tests
    if (!photoFile || typeof URL.createObjectURL !== "function") {
      setPhotoUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(photoFile);
    setPhotoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photoFile]);

  // The AI affordances (auto-prefill + passport scan) are hidden without a configured provider; the
  // plain photo upload keeps working (it still becomes the primary Dier photo).
  const { data: aiAvailResp } = useCustom({
    url: "intake/reference/photo/ai-availability",
    method: "get",
  });
  const aiAvailable = (aiAvailResp?.data as { available?: boolean } | undefined)?.available === true;

  // Reference feeds (same routes the Basisinformatie editor uses); breeds follow the chosen species.
  const species = useFeed<OptionRow>("animals/reference/species");
  const coats = useFeed<OptionRow>("animals/reference/coats");
  const colours = useFeed<OptionRow>("animals/reference/colours");

  // New-animal draft (details phase, no existing animal).
  const [draft, setDraft] = useState<Criteria>(EMPTY_CRITERIA);
  // A chosen existing animal is EDITABLE here (Jmix AnimalDetailsFragment): load its full record so the
  // form starts from the real ids (the search rows only carry labels) and we hold its lock version.
  const [existingVersion, setExistingVersion] = useState<number | null>(null);
  const existingDetail = useCustom({
    url: `animals/${detailsAnimal?.id ?? 0}`,
    method: "get",
    queryOptions: { enabled: phase === "details" && detailsAnimal != null },
  });
  const existingLoading = detailsAnimal != null && existingDetail.isFetching && existingVersion == null;
  // The chosen animal's own photo (authed blob url, falling back to the plain url) for the right panel.
  const existingPhotoUrl =
    detailsAnimal == null ? null : (resultPhotoUrls[String(detailsAnimal.id)] ?? detailsAnimal.fotoUrl);
  const { mutate: saveAnimal } = useCustomMutation();

  useEffect(() => {
    if (phase !== "details" || detailsAnimal == null) return;
    const d = existingDetail.data?.data as AnimalDetail | undefined;
    if (!d || d.id !== detailsAnimal.id) return;
    setDraft({
      naam: d.naam ?? "",
      chip: d.chipPasnummer ?? "",
      geslacht: d.geslacht ?? "",
      gecastreerd: d.gecastreerd ?? false,
      geboortedatum: d.geboortedatum ?? "",
      diersoortId: d.diersoortId != null ? String(d.diersoortId) : "",
      rasId: d.rasId != null ? String(d.rasId) : "",
      kruising: d.kruising ?? false,
      vachtId: d.vachtId != null ? String(d.vachtId) : "",
      kleurId: d.kleurId != null ? String(d.kleurId) : "",
    });
    setExistingVersion(d.version ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, detailsAnimal, existingDetail.data]);
  const draftBreeds = useFeed<OptionRow>(
    `animals/reference/breeds${draft.diersoortId ? `?species=${draft.diersoortId}` : ""}`,
    phase === "details" && detailsAnimal == null,
  );
  const criteriaBreeds = useFeed<OptionRow>(
    `animals/reference/breeds${criteria.diersoortId ? `?species=${criteria.diersoortId}` : ""}`,
    phase === "search",
  );

  // Server-side animal search. The `dier` table has ~430k rows, so filtering the 200-row list feed
  // client-side could never find most animals — Zoeken now hits GET /api/animals/search with the
  // structured criteria (mirroring the Jmix server-side search). Each Zoeken snapshots the criteria
  // into `pendingSearch` and sets `searchParams` with a bumped nonce (forces a refetch even for
  // identical criteria); when the fetch settles, the snapshot is consumed once to route the result.
  const [searchUrl, setSearchUrl] = useState<string | null>(null);
  const searchNonce = useRef(0);
  const pendingSearch = useRef<Criteria | null>(null);
  const searchResp = useCustom<SubwizardAnimal[]>({
    url: searchUrl ?? "animals/search",
    method: "get",
    queryOptions: { enabled: searchUrl != null },
  });
  const searchBusy = searchUrl != null && searchResp.isFetching;

  useEffect(() => {
    const crit = pendingSearch.current;
    if (!crit || searchUrl == null || searchResp.isFetching) return;
    pendingSearch.current = null;
    const hits = (searchResp.data?.data as SubwizardAnimal[] | undefined) ?? [];
    const chip = crit.chip.replace(/\s/g, "");
    // Jmix: one exact-15-digit chip match auto-selects and skips the results step.
    if (isExactly15Digits(chip) && hits.length === 1 && !residentIds.has(hits[0].id) && !selectedIds.has(hits[0].id)) {
      setSearchNotice(t("inboeken.search.chipAutoSelected"));
      onAddExisting(hits[0], photoFile);
      return;
    }
    if (hits.length === 0) {
      // Jmix: no results → straight into the prefilled new-animal form.
      setDraft({ ...crit });
      setDetailsAnimal(null);
      setFromZeroHits(true);
      setPhase("details");
      return;
    }
    setResults(hits);
    setPhase("results");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResp.isFetching, searchResp.data, searchUrl]);

  // Live duplicate-chip guard (Jmix AnimalDetailsFragment hard block) — only queries an exact chip.
  const draftChip = draft.chip.replace(/\s/g, "");
  const { data: dupResp } = useCustom({
    url: `intake/reference/chip-duplicate?chip=${encodeURIComponent(draftChip)}`,
    method: "get",
    queryOptions: { enabled: phase === "details" && detailsAnimal == null && isExactly15Digits(draftChip) },
  });
  const chipDuplicate = (dupResp?.data as ChipDuplicate | undefined)?.duplicate
    ? (dupResp?.data as ChipDuplicate)
    : null;

  const labelOf = (rows: OptionRow[], id: string) => rows.find((r) => String(r.id) === id)?.label ?? null;

  // Apply AI suggestions to whichever form the operator is looking at: the search criteria, or the
  // new-animal draft in the details phase. EMPTY fields only (mergeAiSuggestions); returns the count
  // a merge applies so the caller can decide on the Jmix "AI heeft … ingevuld." notification.
  function applyAiSuggestions(suggestions: PhotoAiSuggestion[], allowed: Set<string>): number {
    const isDraft = phase === "details" && detailsAnimal == null;
    const { applied } = mergeAiSuggestions(isDraft ? draft : criteria, suggestions, allowed);
    if (applied > 0) {
      const set = isDraft ? setDraft : setCriteria;
      set((c) => mergeAiSuggestions(c, suggestions, allowed).next);
    }
    return applied;
  }

  async function handlePhotoPicked(gekozen: File) {
    setPhotoNotice(null);
    setPhotoError(null);
    // Ook de dierfoto zelf: een HEIC kan de browser niet eens tonen, en deze foto wordt straks de
    // profielfoto van het dier.
    const file = await fotoKlaarzetten(gekozen);
    setPhotoFile(file);
    if (!aiAvailable) return;
    setAiBusy(true);
    try {
      const res = await analyzeIntakePhoto(file);
      if (res.available && applyAiSuggestions(res.suggestions, PHOTO_PREFILL_FIELDS) > 0) {
        setPhotoNotice(t("inboeken.photo.aiPrefillApplied"));
      }
    } catch {
      // AI is best-effort — the photo itself keeps working as the future primary Dier photo
    } finally {
      setAiBusy(false);
    }
  }

  /**
   * Alle bladzijden van het paspoort in één keer.
   *
   * <p>Dit ging eerst bladzijde voor bladzijde, elk met een eigen aanroep. Nieuwe paspoortboekjes
   * hebben er twee met gegevens, en dan ziet geen van beide aanroepen het hele dier: de chip staat op
   * de ene en de geboortedatum op de andere. Nu gaan ze samen als bladzijden van één document --
   * ook goedkoper en sneller dan twee keer vragen.
   */
  async function handlePassportPicked(files: File[]) {
    setPhotoNotice(null);
    setPhotoError(null);
    setAiBusy(true);
    try {
      // Eerst omzetten en verkleinen: een iPhone levert HEIC (dat niets in de keten kan lezen) en
      // bladzijden van tientallen megabytes, die de provider weigert.
      const bladzijden = await Promise.all(files.slice(0, 8).map(fotoKlaarzetten));
      const res = await analyzeIntakePassport(bladzijden);
      applyAiSuggestions(res.suggestions, PASSPORT_PREFILL_FIELDS);
      setPaspoortBladzijden((oud) => [...oud, ...bladzijden.map((f) => f.name)]);
      setPhotoNotice(t("inboeken.photo.aiPrefillApplied"));
    } catch {
      setPhotoError(t("inboeken.photo.passportError"));
    } finally {
      setAiBusy(false);
    }
  }

  const onPickPhoto = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (file) void handlePhotoPicked(file);
  };
  const onPickPassport = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (files.length) void handlePassportPicked(files);
  };

  // The Jmix upload widget (photo preview + upload/camera + the passport-scan button) — shown on the
  // search phase and on the new-animal form (the details phase for an existing animal keeps its own
  // read-only thumb).
  function renderPhotoPanel() {
    if (cameraOn) {
      return (
        <div className="fawkes-animalsub__photo">
          <CameraCapture
            onCapture={(dataUrl) => {
              setCameraOn(false);
              void handlePhotoPicked(dataUrlToFile(dataUrl));
            }}
            onCancel={() => setCameraOn(false)}
            t={t}
          />
        </div>
      );
    }
    return (
      <div className="fawkes-om-modal__panel fawkes-om-modal__photo-panel">
        <div className="fawkes-om-modal__panel-head fawkes-om-modal__panel-head--center">
          <span className="fawkes-om-modal__panel-icon fawkes-om-modal__panel-icon--lg">
            <CameraPawIcon size={22} />
          </span>
          <h3 className="fawkes-om-modal__panel-title">{t("inboeken.photo.upload")}</h3>
          <p className="fawkes-om-modal__panel-sub">{t("inboeken.photo.info")}</p>
        </div>
        <input
          ref={photoInputRef}
          className="fawkes-animalsub__photo-input"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          aria-label={t("inboeken.photo.upload")}
          onChange={onPickPhoto}
        />
        <button
          type="button"
          className={"fawkes-om-modal__dropzone" + (photoUrl ? " fawkes-om-modal__dropzone--has-photo" : "")}
          onClick={() => photoInputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer.files?.[0];
            if (f) void handlePhotoPicked(f);
          }}
        >
          {photoUrl ? (
            <img className="fawkes-om-modal__dropzone-preview" src={photoUrl} alt={t("inboeken.photo.previewAlt")} />
          ) : (
            <>
              <ImageIcon size={30} aria-hidden="true" />
              <span className="fawkes-om-modal__dropzone-title">Klik om een foto te uploaden</span>
              <span className="fawkes-om-modal__dropzone-sub">of sleep een bestand hierheen</span>
            </>
          )}
        </button>
        {aiAvailable && (
          <>
            <div className="fawkes-om-modal__or"><span>of</span></div>
            <input
              ref={passportInputRef}
              className="fawkes-animalsub__photo-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              aria-label={t("inboeken.photo.passportScan")}
              multiple
              onChange={onPickPassport}
            />
            <Button variant="secondary" disabled={aiBusy} onClick={() => passportInputRef.current?.click()}>
              <CameraIcon width={16} height={16} />{" "}
              {aiBusy ? t("animalDetailView.photo.aiAnalyzing") : t("inboeken.photo.passportScan")}
            </Button>
          </>
        )}
        {photoNotice && <span className="fawkes-inboeken__helper" role="status">{photoNotice}</span>}
        {paspoortBladzijden.length > 0 && (
          <ul className="fawkes-inboeken__bladzijden">
            {paspoortBladzijden.map((naam, i) => (
              <li key={`${naam}-${i}`} title={naam}>{naam}</li>
            ))}
          </ul>
        )}
        {photoError && <span className="fawkes-inboeken__field-error" role="alert">{photoError}</span>}
      </div>
    );
  }

  const hasCriterion =
    criteria.naam.trim() !== "" ||
    criteria.chip.trim() !== "" ||
    criteria.geslacht !== "" ||
    criteria.gecastreerd ||
    criteria.geboortedatum !== "" ||
    criteria.diersoortId !== "" ||
    criteria.rasId !== "" ||
    criteria.kruising ||
    criteria.vachtId !== "" ||
    criteria.kleurId !== "";

  function runSearch() {
    if (!hasCriterion) {
      setSearchNotice(t("inboeken.search.minOne"));
      return;
    }
    setSearchNotice(null);
    searchNonce.current += 1;
    pendingSearch.current = { ...criteria };
    // This app's data provider takes query params in the URL string (not config.query), so build the
    // querystring here. Only the set criteria are sent; `_n` forces a refetch even for identical ones.
    const params = new URLSearchParams();
    const chip = criteria.chip.replace(/\s/g, "");
    // Send the reference facts by NAME (the label), not id — the colour/coat/breed reference tables
    // have duplicate rows per label, so an id would only match the animals carrying that exact id.
    const soort = labelOf(species, criteria.diersoortId);
    const ras = labelOf(criteriaBreeds, criteria.rasId);
    const vacht = labelOf(coats, criteria.vachtId);
    const kleur = labelOf(colours, criteria.kleurId);
    if (criteria.naam.trim()) params.set("naam", criteria.naam.trim());
    if (chip) params.set("chip", chip);
    if (soort) params.set("diersoort", soort);
    if (ras) params.set("ras", ras);
    if (vacht) params.set("vacht", vacht);
    if (kleur) params.set("kleur", kleur);
    if (criteria.geslacht) params.set("geslacht", criteria.geslacht);
    if (criteria.geboortedatum) params.set("geboortedatum", criteria.geboortedatum);
    if (criteria.gecastreerd) params.set("gecastreerd", "true");
    if (criteria.kruising) params.set("kruising", "true");
    params.set("_n", String(searchNonce.current));
    setSearchUrl(`animals/search?${params.toString()}`);
  }

  function openNewAnimal() {
    setDraft({ ...criteria });
    setDetailsAnimal(null);
    setFromZeroHits(false);
    setPhase("details");
  }

  function confirmDetails() {
    if (detailsAnimal) {
      // The form is editable for an existing animal too — persist whatever was corrected before
      // pulling it into the intake (Jmix saves the AnimalDetailsFragment on Bevestig dier).
      saveAnimal(
        {
          url: `animals/${detailsAnimal.id}`,
          method: "put",
          values: {
            naam: draft.naam.trim(),
            geslacht: draft.geslacht || null,
            gecastreerd: draft.gecastreerd,
            diersoortId: draft.diersoortId ? Number(draft.diersoortId) : null,
            rasId: draft.rasId ? Number(draft.rasId) : null,
            kruising: draft.kruising,
            dierVachtId: draft.vachtId ? Number(draft.vachtId) : null,
            dierKleurId: draft.kleurId ? Number(draft.kleurId) : null,
            chipPasnummer: draftChip || null,
            geboortedatum: draft.geboortedatum || null,
            version: existingVersion,
          },
        },
        { onSettled: () => undefined },
      );
      // The intake keeps the animal's identity; the edited labels ride along in the summary row.
      onAddExisting(
        {
          ...detailsAnimal,
          naam: draft.naam.trim() || detailsAnimal.naam,
          chipPasnummer: draftChip || detailsAnimal.chipPasnummer,
          geslacht: draft.geslacht || detailsAnimal.geslacht,
          geboortedatum: draft.geboortedatum || detailsAnimal.geboortedatum,
          diersoort: labelOf(species, draft.diersoortId) ?? detailsAnimal.diersoort,
          ras: labelOf(draftBreeds, draft.rasId) ?? detailsAnimal.ras,
          vacht: labelOf(coats, draft.vachtId) ?? detailsAnimal.vacht,
          kleur: labelOf(colours, draft.kleurId) ?? detailsAnimal.kleur,
          kruising: draft.kruising,
          gecastreerd: draft.gecastreerd,
        },
        photoFile,
      );
      return;
    }
    onAddNew({
      naam: draft.naam.trim(),
      diersoortId: Number(draft.diersoortId),
      diersoortLabel: labelOf(species, draft.diersoortId),
      geslacht: draft.geslacht || null,
      geboortedatum: draft.geboortedatum || null,
      chipPasnummer: draftChip || null,
      rasId: draft.rasId ? Number(draft.rasId) : null,
      rasLabel: labelOf(draftBreeds, draft.rasId),
      kruising: draft.kruising,
      vachtId: draft.vachtId ? Number(draft.vachtId) : null,
      vachtLabel: labelOf(coats, draft.vachtId),
      kleurId: draft.kleurId ? Number(draft.kleurId) : null,
      kleurLabel: labelOf(colours, draft.kleurId),
      gecastreerd: draft.gecastreerd,
    }, photoFile);
  }

  // Details-phase gates — the verbatim Jmix AnimalDetailsFragment validations.
  const chipFormatInvalid = draftChip !== "" && !isExactly15Digits(draftChip);
  const naamMissing = detailsAnimal == null && !draft.naam.trim();
  const soortMissing = detailsAnimal == null && !draft.diersoortId;
  const detailsBlocked =
    detailsAnimal == null && (naamMissing || soortMissing || chipFormatInvalid || chipDuplicate != null);

  function renderSearch() {
    return (
      <div className="fawkes-om-modal__cols">
        {/* LEFT — search form panel */}
        <div className="fawkes-om-modal__panel">
          <div className="fawkes-om-modal__panel-head">
            <span className="fawkes-om-modal__panel-icon"><Search size={18} aria-hidden="true" /></span>
            <h3 className="fawkes-om-modal__panel-title">{t("inboeken.search.header")}</h3>
          </div>
          <div className="fawkes-om-modal__form">
            <Field label={t("inboeken.search.naam")} value={criteria.naam}
              placeholder={t("inboeken.search.naam")}
              onChange={(e) => setCriteria((c) => ({ ...c, naam: e.target.value }))} />
            <Field label={t("inboeken.newAnimal.chip")} value={criteria.chip}
              placeholder={t("inboeken.newAnimal.chip.placeholder")} maxLength={15}
              onChange={(e) => setCriteria((c) => ({ ...c, chip: e.target.value }))} />
            <div className="fawkes-om-modal__form-row">
              <SelectField label={t("inboeken.newAnimal.geslacht")} value={criteria.geslacht}
                onChange={(e) => setCriteria((c) => ({ ...c, geslacht: e.target.value }))}
                options={[{ value: "", label: t("inboeken.newAnimal.geslacht.placeholder") },
                  ...GESLACHTEN.map((g) => ({ value: g, label: g }))]} />
              <label className="fawkes-om-modal__switch">
                <input type="checkbox" checked={criteria.gecastreerd}
                  onChange={(e) => setCriteria((c) => ({ ...c, gecastreerd: e.target.checked }))} />
                <span className="fawkes-om-modal__switch-track" aria-hidden="true" />
                <span className="fawkes-om-modal__switch-label">{t("inboeken.newAnimal.casSter")}</span>
              </label>
              <Field label={t("inboeken.field.geboortedatum")} type="date" value={criteria.geboortedatum}
                onChange={(e) => setCriteria((c) => ({ ...c, geboortedatum: e.target.value }))} />
            </div>
            <div className="fawkes-om-modal__form-row">
              <ComboBox label={t("inboeken.newAnimal.diersoort")} value={criteria.diersoortId}
                onChange={(v) => setCriteria((c) => ({ ...c, diersoortId: v, rasId: "" }))}
                placeholder={t("inboeken.newAnimal.diersoort.placeholder")}
                options={species.map((s) => ({ value: String(s.id), label: s.label }))} />
              <ComboBox label={t("inboeken.newAnimal.ras")} value={criteria.rasId}
                onChange={(v) => {
                  const breed = criteriaBreeds.find((r) => String(r.id) === v);
                  setCriteria((c) => ({
                    ...c,
                    rasId: v,
                    // Jmix Ras→Diersoort: picking a breed fills the species if none chosen yet.
                    diersoortId: c.diersoortId || (breed?.speciesId != null ? String(breed.speciesId) : ""),
                  }));
                }}
                placeholder={t("inboeken.newAnimal.ras.placeholder")}
                options={criteriaBreeds.map((r) => ({ value: String(r.id), label: r.label }))} />
              <label className="fawkes-om-modal__switch">
                <input type="checkbox" checked={criteria.kruising}
                  onChange={(e) => setCriteria((c) => ({ ...c, kruising: e.target.checked }))} />
                <span className="fawkes-om-modal__switch-track" aria-hidden="true" />
                <span className="fawkes-om-modal__switch-label">{t("inboeken.newAnimal.kruising")}</span>
              </label>
            </div>
            <div className="fawkes-om-modal__form-row">
              <ComboBox label={t("inboeken.newAnimal.vacht")} value={criteria.vachtId}
                onChange={(v) => setCriteria((c) => ({ ...c, vachtId: v }))}
                placeholder={t("inboeken.newAnimal.vacht.placeholder")}
                options={coats.map((v) => ({ value: String(v.id), label: v.label }))} />
              <ComboBox label={t("inboeken.newAnimal.kleur")} value={criteria.kleurId}
                onChange={(v) => setCriteria((c) => ({ ...c, kleurId: v }))}
                placeholder={t("inboeken.newAnimal.kleur.placeholder")}
                options={colours.map((k) => ({ value: String(k.id), label: k.label }))} />
            </div>
          </div>
          {searchNotice && <span className="fawkes-inboeken__field-error" role="alert">{searchNotice}</span>}
          <div className="fawkes-om-modal__panel-actions">
            <Button onClick={runSearch} disabled={searchBusy}>
              {searchBusy ? t("common.loading") : `${t("inboeken.search.button")} ›`}
            </Button>
          </div>
        </div>
        {/* RIGHT — photo upload panel */}
        {renderPhotoPanel()}
      </div>
    );
  }

  function selectResult(a: SubwizardAnimal) {
    setDetailsAnimal(a);
    setFromZeroHits(false);
    setPhase("details");
  }

  function renderResults() {
    return (
      <div className="fawkes-animalsub">
        {/* Jmix results top bar: back, title, view-mode switch, and a prominent "Nieuw dier" action. */}
        <div className="fawkes-animalsub__results-bar">
          <button type="button" className="fawkes-inboeken__link" onClick={() => setPhase("search")}>
            ‹ {t("common.back")}
          </button>
          <span className="fawkes-animalsub__results-title">{t("inboeken.results.header")}</span>
          <span className="fawkes-animalsub__results-bar-spacer" />
          <div className="fawkes-animalsub__viewtoggle" role="group" aria-label={t("inboeken.results.view")}>
            <button
              type="button"
              className={resultsView === "cards" ? "is-active" : ""}
              aria-pressed={resultsView === "cards"}
              title={t("inboeken.results.cardView")}
              aria-label={t("inboeken.results.cardView")}
              onClick={() => setResultsView("cards")}
            >
              <LayoutGrid size={16} aria-hidden="true" />
            </button>
            <button
              type="button"
              className={resultsView === "list" ? "is-active" : ""}
              aria-pressed={resultsView === "list"}
              title={t("inboeken.results.listView")}
              aria-label={t("inboeken.results.listView")}
              onClick={() => setResultsView("list")}
            >
              <List size={16} aria-hidden="true" />
            </button>
          </div>
          <Button variant="primary" onClick={openNewAnimal}>
            <PlusCircleSolidIcon width={18} height={18} /> {t("inboeken.newAnimal.start")}
          </Button>
        </div>
        {results.length === 0 ? (
          <span className="fawkes-inboeken__muted">{t("inboeken.results.none")}</span>
        ) : resultsView === "cards" ? (
          <ul className="fawkes-animalsub__results-grid">
            {results.map((a) => {
              const resident = residentIds.has(a.id);
              const already = selectedIds.has(a.id);
              const blocked = resident || already;
              // Prefer the authenticated uploaded photo; fall back to the online url, then a placeholder.
              const photo = resultPhotoUrls[String(a.id)] ?? a.fotoUrl;
              return (
                <li key={a.id} className={"fawkes-animalsub__result" + (blocked ? " is-blocked" : "")}>
                  <div className="fawkes-animalsub__result-media">
                    {photo ? (
                      <img src={photo} alt={a.naam} />
                    ) : (
                      <span
                        className="fawkes-animalsub__result-media-empty"
                        aria-label={t("inboeken.results.noPhoto")}
                        title={t("inboeken.results.noPhoto")}
                      />
                    )}
                  </div>
                  <div className="fawkes-animalsub__result-body">
                    <strong className="fawkes-animalsub__result-name">{a.naam || "—"}</strong>
                    <span className="fawkes-animalsub__result-line">
                      {[a.chipPasnummer, a.diersoort, a.ras].filter(Boolean).join(" · ") || "—"}
                    </span>
                    <span className="fawkes-animalsub__result-line">
                      {[a.geslacht, a.kleur, a.vacht].filter(Boolean).join(" · ") || "—"}
                    </span>
                    {resident && <span className="fawkes-inboeken__muted">{t("inboeken.animals.openStay")}</span>}
                    {!resident && already && (
                      <span className="fawkes-inboeken__muted">{t("inboeken.results.alreadySelected")}</span>
                    )}
                    <Button variant="secondary" disabled={blocked} onClick={() => selectResult(a)}>
                      {t("inboeken.results.select")}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="fawkes-animalsub__results-table-wrap">
            <table className="fawkes-animalsub__results-table">
              <thead>
                <tr>
                  <th className="fawkes-animalsub__results-table-thumb" aria-hidden="true" />
                  <th>{t("inboeken.newAnimal.chip")}</th>
                  <th>{t("inboeken.newAnimal.naam")}</th>
                  <th>{t("inboeken.newAnimal.diersoort")}</th>
                  <th>{t("inboeken.newAnimal.ras")}</th>
                  <th>{t("inboeken.newAnimal.geslacht")}</th>
                  <th>{t("inboeken.newAnimal.kleur")}</th>
                  <th>{t("inboeken.newAnimal.vacht")}</th>
                  <th aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {results.map((a) => {
                  const resident = residentIds.has(a.id);
                  const already = selectedIds.has(a.id);
                  const blocked = resident || already;
                  const photo = resultPhotoUrls[String(a.id)] ?? a.fotoUrl;
                  return (
                    <tr key={a.id} className={blocked ? "is-blocked" : ""}>
                      <td className="fawkes-animalsub__results-table-thumb">
                        {photo ? (
                          <img src={photo} alt="" />
                        ) : (
                          <span
                            className="fawkes-animalsub__results-table-thumb-empty"
                            aria-label={t("inboeken.results.noPhoto")}
                          />
                        )}
                      </td>
                      <td>{a.chipPasnummer ?? "—"}</td>
                      <td>{a.naam || "—"}</td>
                      <td>{a.diersoort ?? "—"}</td>
                      <td>{a.ras ?? "—"}</td>
                      <td>{a.geslacht ?? "—"}</td>
                      <td>{a.kleur ?? "—"}</td>
                      <td>{a.vacht ?? "—"}</td>
                      <td className="fawkes-animalsub__results-table-action">
                        <Button variant="secondary" disabled={blocked} onClick={() => selectResult(a)}>
                          {t("inboeken.results.select")}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  function renderDetails() {
    const existing = detailsAnimal;
    return (
      <div className="fawkes-animalsub">
        <span className="fawkes-inboeken__step-title">{t("inboeken.details.header")}</span>
        {existing == null && fromZeroHits && (
          <span className="fawkes-inboeken__helper">{t("inboeken.details.newBanner")}</span>
        )}
        {existing != null && existingLoading && (
          <span className="fawkes-inboeken__muted">{t("common.loading")}</span>
        )}
        {/* Both an existing animal and a brand-new one edit the SAME form (Jmix AnimalDetailsFragment):
            a chosen animal is loaded into these fields so it can be corrected/completed here, and the
            edits are saved on Bevestigen. Jmix layout: form left, photo panel right. */}
        <div className="fawkes-om-modal__cols">
          {/* LEFT — the animal form: the SAME panel layout as Zoek dier. An existing animal is loaded
              into these fields (Jmix AnimalDetailsFragment) so it can be corrected here; the edits are
              saved on Bevestig dier. */}
          <div className="fawkes-om-modal__panel">
            <div className="fawkes-om-modal__panel-head">
              <span className="fawkes-om-modal__panel-icon"><PawPrint size={18} aria-hidden="true" /></span>
              <h3 className="fawkes-om-modal__panel-title">{t("inboeken.details.header")}</h3>
            </div>
            <div className="fawkes-om-modal__form">
              <div className="fawkes-inboeken__field">
                <Field label={t("inboeken.newAnimal.naam")} value={draft.naam}
                  onChange={(e) => setDraft((d) => ({ ...d, naam: e.target.value }))} />
                {naamMissing && (
                  <span className="fawkes-inboeken__field-error">{t("inboeken.newAnimal.naamRequired")}</span>
                )}
              </div>
              <div className="fawkes-inboeken__field">
                <Field label={t("inboeken.newAnimal.chip")} value={draft.chip}
                  placeholder={t("inboeken.newAnimal.chip.placeholder")} maxLength={15}
                  onChange={(e) => setDraft((d) => ({ ...d, chip: e.target.value }))} />
                {chipFormatInvalid ? (
                  <span className="fawkes-inboeken__field-error">{t("inboeken.newAnimal.chipFormat")}</span>
                ) : chipDuplicate ? (
                  <div className="fawkes-inboeken__field">
                    <span className="fawkes-inboeken__field-error">
                      {t("inboeken.newAnimal.chipDuplicate", { args: [draftChip, chipDuplicate.naam ?? "?"] })}
                    </span>
                    {(() => {
                      const dupe = animals.find((x) => x.id === chipDuplicate.id);
                      return dupe ? (
                        <Button variant="secondary" onClick={() => onAddExisting(dupe, photoFile)}>
                          {t("inboeken.newAnimal.chipDuplicate.use")}
                        </Button>
                      ) : null;
                    })()}
                  </div>
                ) : null}
              </div>
              <div className="fawkes-om-modal__form-row">
                <SelectField label={t("inboeken.newAnimal.geslacht")} value={draft.geslacht}
                  onChange={(e) => setDraft((d) => ({ ...d, geslacht: e.target.value }))}
                  options={[{ value: "", label: t("inboeken.newAnimal.geslacht.placeholder") },
                    ...GESLACHTEN.map((g) => ({ value: g, label: g }))]} />
                <label className="fawkes-om-modal__switch">
                  <input type="checkbox" checked={draft.gecastreerd}
                    onChange={(e) => setDraft((d) => ({ ...d, gecastreerd: e.target.checked }))} />
                  <span className="fawkes-om-modal__switch-track" aria-hidden="true" />
                  <span className="fawkes-om-modal__switch-label">{t("inboeken.newAnimal.casSter")}</span>
                </label>
                <Field label={t("inboeken.field.geboortedatum")} type="date" value={draft.geboortedatum}
                  onChange={(e) => setDraft((d) => ({ ...d, geboortedatum: e.target.value }))} />
              </div>
              <div className="fawkes-om-modal__form-row">
                <div className="fawkes-inboeken__field">
                  <ComboBox label={t("inboeken.newAnimal.diersoort")} value={draft.diersoortId}
                    onChange={(v) => setDraft((d) => ({ ...d, diersoortId: v, rasId: "" }))}
                    placeholder={t("inboeken.newAnimal.diersoort.placeholder")}
                    invalid={soortMissing}
                    options={species.map((s) => ({ value: String(s.id), label: s.label }))} />
                  {soortMissing && (
                    <span className="fawkes-inboeken__field-error">{t("inboeken.newAnimal.diersoortRequired")}</span>
                  )}
                </div>
                <ComboBox label={t("inboeken.newAnimal.ras")} value={draft.rasId}
                  onChange={(v) => {
                    const breed = draftBreeds.find((r) => String(r.id) === v);
                    setDraft((d) => ({
                      ...d,
                      rasId: v,
                      diersoortId: d.diersoortId || (breed?.speciesId != null ? String(breed.speciesId) : ""),
                    }));
                  }}
                  placeholder={t("inboeken.newAnimal.ras.placeholder")}
                  options={draftBreeds.map((r) => ({ value: String(r.id), label: r.label }))} />
                <label className="fawkes-om-modal__switch">
                  <input type="checkbox" checked={draft.kruising}
                    onChange={(e) => setDraft((d) => ({ ...d, kruising: e.target.checked }))} />
                  <span className="fawkes-om-modal__switch-track" aria-hidden="true" />
                  <span className="fawkes-om-modal__switch-label">{t("inboeken.newAnimal.kruising")}</span>
                </label>
              </div>
              <div className="fawkes-om-modal__form-row">
                <ComboBox label={t("inboeken.newAnimal.vacht")} value={draft.vachtId}
                  onChange={(v) => setDraft((d) => ({ ...d, vachtId: v }))}
                  placeholder={t("inboeken.newAnimal.vacht.placeholder")}
                  options={coats.map((v) => ({ value: String(v.id), label: v.label }))} />
                <ComboBox label={t("inboeken.newAnimal.kleur")} value={draft.kleurId}
                  onChange={(v) => setDraft((d) => ({ ...d, kleurId: v }))}
                  placeholder={t("inboeken.newAnimal.kleur.placeholder")}
                  options={colours.map((k) => ({ value: String(k.id), label: k.label }))} />
              </div>
            </div>
          </div>
          {/* RIGHT — the animal photo when it already has one, otherwise the upload panel. */}
          {existingPhotoUrl ? (
            <div className="fawkes-om-modal__panel fawkes-om-modal__photo-panel">
              <img className="fawkes-om-modal__dropzone-preview" src={existingPhotoUrl} alt={draft.naam} />
            </div>
          ) : (
            renderPhotoPanel()
          )}
        </div>
        <div className="fawkes-animalsub__actions">
          <Button
            variant="primary"
            onClick={() => (fromZeroHits ? setPhase("search") : setPhase(existing ? "results" : results.length ? "results" : "search"))}
          >
            ‹ {t("common.back")}
          </Button>
          <span className="fawkes-stepwiz__actions-spacer" />
          <Button onClick={confirmDetails} disabled={detailsBlocked}>
            {t("inboeken.details.confirm")} ›
          </Button>
        </div>
      </div>
    );
  }

  return phase === "search" ? renderSearch() : phase === "results" ? renderResults() : renderDetails();
}

/** The 7-field Jmix summary-row text: chip | naam | soort | ras | geslacht | kleur | vacht. */
export function animalRowText(parts: {
  chip?: string | null;
  naam: string;
  soort?: string | null;
  ras?: string | null;
  geslacht?: string | null;
  kleur?: string | null;
  vacht?: string | null;
}): string {
  // Design recap row: the present facts joined by " · " (drop empty fields; no "-" placeholders).
  return (
    [parts.chip, parts.naam, parts.soort, parts.ras, parts.geslacht, parts.kleur, parts.vacht]
      .filter((v) => v != null && v !== "")
      .join(" · ") || parts.naam || "—"
  );
}
